/* cpap-edge-personalisation.js
 *
 * Calls worker.cpapdiscount.au/geo (Cloudflare Worker) to get the visitor's
 * geo + support-hours flag, then progressively enhances:
 *   1. Stock badge — "Fast shipping from our AU warehouse" -> "to {STATE}" for AU visitors with a known state
 *   2. Support-hours pill — "Live — chat with our team" during Adelaide 9-5 Mon-Fri
 *
 * Logged-in customers already get a server-rendered personalised badge via
 * Liquid (`customer.default_address.province`). Their data-ship-location
 * is non-default, so we skip the swap and respect what Liquid emitted.
 *
 * Fails silently — if the Worker is down, the page keeps the static fallback.
 */
(function () {
  'use strict';

  var GEO_ENDPOINT = 'https://worker.cpapdiscount.au/geo';
  // sessionStorage cache key — avoid hitting the Worker on every PDP within a session.
  var CACHE_KEY = 'cdw_geo_v1';
  var CACHE_TTL_MS = 30 * 60 * 1000; // 30 min

  // ISO 3166-2 AU state codes -> friendly names. Kept in sync with the Worker.
  var STATE_NAMES = {
    NSW: 'New South Wales',
    VIC: 'Victoria',
    QLD: 'Queensland',
    WA: 'Western Australia',
    SA: 'South Australia',
    TAS: 'Tasmania',
    ACT: 'Australian Capital Territory',
    NT: 'Northern Territory'
  };

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (!c || !c.t || Date.now() - c.t > CACHE_TTL_MS) return null;
      return c.d;
    } catch (e) {
      return null;
    }
  }

  function writeCache(d) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d: d }));
    } catch (e) { /* private mode etc. — ignore */ }
  }

  function fetchGeo() {
    var cached = readCache();
    if (cached) return Promise.resolve(cached);
    return fetch(GEO_ENDPOINT, { credentials: 'omit', mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { if (d) writeCache(d); return d; })
      .catch(function () { return null; });
  }

  // --- 1. Stock badge swap ----------------------------------------------
  // Policy: only personalise when we're high-confidence. IP-geo at AU state
  // level is ~85% accurate — when wrong (15%), naming the wrong state damages
  // trust more than no personalisation. So we only swap in two cases:
  //   (a) Liquid already rendered "to <province>" for a logged-in customer — leave alone
  //   (b) Cloudflare's isAdelaide flag is true (regionCode=SA + city≈Adelaide)
  //       — replace the generic "across Australia" with "from Adelaide" for our locals
  // All other anonymous traffic keeps the neutral "across Australia" default.
  function swapStockBadge(geo) {
    if (!geo || !geo.isAdelaide) return;
    var newLoc = 'from Adelaide';

    document.querySelectorAll('[data-stock-in], .stock--ok, .stock.in').forEach(function (el) {
      // Skip if logged-in customer already got a personalised badge from Liquid.
      var current = (el.getAttribute('data-ship-location') || '').trim();
      if (current && current.indexOf('to ') === 0) return;

      // Update the data attribute so the variant-change JS handler (cpap-mask/part-pdp)
      // re-renders with the same personalisation.
      el.setAttribute('data-ship-location', newLoc);

      el.innerHTML = el.innerHTML.replace(
        /fast shipping across Australia/i,
        'fast shipping ' + newLoc
      );
    });
  }

  // --- 2. Support-hours indicator ---------------------------------------
  // Looks for an opt-in placeholder element [data-support-hours] anywhere on
  // the page and fills it. PDPs can drop the placeholder in the buy column;
  // contact / chatbot CTA can use the same pattern. Doesn't render anything
  // if the placeholder isn't present, so it's a no-op until the theme opts in.
  function renderSupportHours(geo) {
    var slots = document.querySelectorAll('[data-support-hours]');
    if (!slots.length) return;
    var open = geo && geo.supportOpen === true;
    var html = open
      ? '<span class="support-hours support-hours--open" role="status"><span class="dot"></span> Our team is online now — <a href="tel:+61881660816">08 8166 0816</a></span>'
      : '<span class="support-hours support-hours--closed" role="status"><span class="dot"></span> Support closed — back 9 am Adelaide time</span>';
    slots.forEach(function (slot) { slot.innerHTML = html; });
  }

  // --- Boot -------------------------------------------------------------
  function boot() {
    fetchGeo().then(function (geo) {
      if (!geo) return;
      swapStockBadge(geo);
      renderSupportHours(geo);
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
